import { isAbortError } from '@/utils/abort-util'
import { type JobSearchViewModelFactory } from '@/features/jobs/list/JobSearchViewModel'
import { type SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import type { GetCurrentMemberFn, GetCurrentUserFn } from '@/features/iam'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import { type SelectFilter } from '@/infra/SelectFilter'
import { type JobProgressionStepFilterOption } from '@/features/jobs/list/job-progression-filter-option'
import { type JobStatsStore } from '@/features/jobs/dashboard/job-stats/JobStatsStore'
import { type JobsTotalPricing } from '@/features/jobs/dashboard/job-stats/job-totals'
import { Id } from '@/infra/id'
import {
  JobSearchQueryStringBuilder,
  isNotJobStatusClosed,
} from '@/features/jobs/list/JobSearchQueryStringBuilder'
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { clock, withAbortSignal } from '@/utils/rx-util'
import { timeago } from '@/utils/date-util'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { computePhaseOfDay } from '@/features/jobs/dashboard/greeting-util'
import { fromStream, tapAction, toStream } from '@/utils/mobx-util'
import { type FirmDashboardContractViewModel } from '@/features/jobs/dashboard/FirmDashboardContractViewModel'

/**
 * View model for managing the firm dashboard overview sidebar.
 */
export class FirmDashboardOverviewSidebarViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Emits the phase of day (Morning, Afternoon, Evening) in real-time.
   */
  private readonly _phaseOfDay$ = clock('minute').pipe(
    map(computePhaseOfDay),
    distinctUntilChanged(),
  )

  /**
   * RxJS Observables as MobX observables.
   */
  private readonly _$ = {
    phaseOfDay: fromStream(computePhaseOfDay(new Date()), this._phaseOfDay$),
  }

  /**
   * Total sum for submitted and closed jobs pricing.
   */
  @observable
  accessor jobsTotalPricing: JobsTotalPricing = {
    submittedJobsTotalPrice: 0,
    closedJobsTotalPrice: 0,
  }

  /**
   * Total count for jobs with `OPEN_ITEMS` progression.
   */
  @observable
  accessor openItemsJobsCount = 0

  /**
   * Total count for jobs with `DRAFT_IN_REVIEW` progression.
   */
  @observable
  accessor draftReviewJobsCount = 0

  /**
   * Job Searcher.
   */
  readonly searcher

  constructor(
    private readonly _searchableJobStore: SearchableJobStore,
    createJobSearchViewModel: JobSearchViewModelFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    createFilterStorage: FilterStorageFactory,
    private readonly _jobStatsStore: JobStatsStore,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _getWorkspace: () => Workspace,
    readonly contractVm: FirmDashboardContractViewModel,
  ) {
    this.searcher = createJobSearchViewModel({
      filterStorage: createFilterStorage('FirmDashboardOverviewSidebar'),
    })

    const workspace$ = toStream(() => this._getWorkspace(), true).pipe(
      filter((workspace) => Id.some(workspace.id)),
    )

    this._lifecycle
      .manage(
        // Loading saved filters clears the workspace filter, so it is
        // restored whenever it drifts from the selection.
        workspace$.pipe(
          switchMap((workspace) =>
            toStream(() => this.workspaceFilter.value[0], true).pipe(
              map(() => workspace),
            ),
          ),
          tapAction((workspace) => this._applyWorkspace(workspace)),
        ),
      )
      .manage(
        workspace$.pipe(
          switchMap((workspace) =>
            this._searchableJobStore.jobEvents$.pipe(
              filter(
                (event) =>
                  event.type === 'JobUpdated' &&
                  event.job.workspace.id === workspace.id,
              ),
              // The searcher ignores updates once a filter is applied, and
              // this list always filters.
              tapAction((event) => this.searcher.scroller.prepend(event.job)),
              startWith(null),
              switchMap(() =>
                withAbortSignal((signal) =>
                  this._fetchJobsTotal(workspace, signal),
                ).pipe(catchError(() => EMPTY)),
              ),
            ),
          ),
        ),
      )
      .manage(this.searcher)
  }

  /**
   * The current phase of day.
   */
  @computed
  get phaseOfDay() {
    return this._$.phaseOfDay()
  }

  /**
   * The given name of the current user logged in.
   */
  @computed
  get userName() {
    return this._getCurrentUser().givenName
  }

  /**
   * Whether the member may see the contract amount.
   */
  @computed
  get canViewContractAmount() {
    return (
      this.contractVm.isContractConfigEnabled &&
      this._getCurrentMember().permissions.viewContractAmount
    )
  }

  /**
   * Contract usage, total + all the fees
   */
  @computed
  get contractUsage() {
    return (
      this.jobsTotalPricing.submittedJobsTotalPrice +
      this.jobsTotalPricing.closedJobsTotalPrice +
      this.contractVm.feeAmount
    )
  }

  /**
   * The list of jobs that comes through the searcher.
   */
  @computed
  get jobs() {
    return this.searcher.jobs
  }

  /**
   * Filtered jobs by `OPEN_ITEMS` progression.
   */
  @computed
  get openItemsJobs() {
    return this.jobs.filter(
      (job) => job.progressionStep === 'OpenItems' && job.status !== 'Closed',
    )
  }

  /**
   * Filtered jobs by `DRAFT_IN_REVIEW` progression.
   */
  @computed
  get draftInReviewJobs() {
    return this.jobs.filter(
      (job) =>
        job.progressionStep === 'DraftInReview' && job.status !== 'Closed',
    )
  }

  /**
   * Gets the workspace filter.
   */
  @computed
  get workspaceFilter() {
    return this.searcher.filters.getFilter('workspaces') as SelectFilter<
      string,
      Id
    >
  }

  /**
   * How long ago the job was last updated.
   */
  lastUpdateOf(job: SearchableJob) {
    const lastUpdate = timeago(job.dateModified ?? job.dateCreated, 'long')
    return lastUpdate !== 'now' ? `${lastUpdate} ago` : lastUpdate
  }

  /**
   * The members whose avatars represent the job: its pros for a client who
   * has any, otherwise its clients.
   */
  avatarMembersOf(job: SearchableJob) {
    return job.currentUserMember?.type === 'Client' && job.proMembers.length
      ? job.proMembers
      : job.clientMembers
  }

  /** @inheritDoc */
  activate() {
    this._prepareSearcher()
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Filters the jobs to the workspace and its contract period.
   */
  @action.bound
  private _applyWorkspace(workspace: Workspace) {
    if (this.workspaceFilter.value[0] !== workspace.id) {
      this.workspaceFilter.change([workspace.id])
    }
    this.searcher.setForcedCreatedDateRange({
      from: this.contractVm.contractStartDate,
      to: this.contractVm.contractEndDate,
    })
  }

  /**
   * 1. Fetch the total sum for submitted and closed jobs pricing.
   * 2. Fetch the total count for jobs with progression:
   * `OPEN_ITEMS`, `DRAFT_IN_REVIEW`
   */
  private async _fetchJobsTotal(workspace: Workspace, signal: AbortSignal) {
    const totals = await Promise.all([
      this._jobStatsStore.fetchJobsTotalPricing(
        {
          workspaceId: workspace.id,
          contractStartDate: this.contractVm.contractStartDate,
          contractEndDate: this.contractVm.contractEndDate,
        },
        signal,
      ),
      this._searchableJobStore.fetchTotal(
        {
          scope: 'All',
          filter: new JobSearchQueryStringBuilder()
            .withWorkspaceIds([workspace.id])
            .withProgressionSteps(['OpenItems'])
            .withinDateCreatedRange({
              from: this.contractVm.contractStartDate,
              to: this.contractVm.contractEndDate,
            })
            .withStatuses(isNotJobStatusClosed)
            .build(),
          limit: 0,
        },
        signal,
      ),
      this._searchableJobStore.fetchTotal(
        {
          scope: 'All',
          filter: new JobSearchQueryStringBuilder()
            .withWorkspaceIds([workspace.id])
            .withProgressionSteps(['DraftInReview'])
            .withinDateCreatedRange({
              from: this.contractVm.contractStartDate,
              to: this.contractVm.contractEndDate,
            })
            .withStatuses(isNotJobStatusClosed)
            .build(),
          limit: 0,
        },
        signal,
      ),
    ])
      // Members without pricing access get a permission error, so the
      // totals keep their zero values instead of alerting.
      .catch((err: unknown) => {
        if (isAbortError(err)) {
          throw err
        }
        return null
      })
    signal.throwIfAborted()
    if (!totals) {
      return
    }

    const [jobsTotalPricing, openItemsCount, draftItemsCount] = totals
    runInAction(() => {
      this.jobsTotalPricing = jobsTotalPricing
      this.openItemsJobsCount = openItemsCount
      this.draftReviewJobsCount = draftItemsCount
    })
  }

  /**
   * Narrows the searcher to the progression steps the sidebar lists. The
   * searcher loads its saved filters on activation, so this runs before; the
   * saved progression filter can only hold these same steps.
   */
  private _prepareSearcher() {
    this.searcher.filters.makeFilterImplicit('progressionStep')

    const progressionFilter = this.searcher.filters.getFilter(
      'progressionStep',
    ) as SelectFilter<string, JobProgressionStepFilterOption>

    progressionFilter.selected = ['OpenItems', 'DraftInReview']
  }
}
